/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { Reducer } from 'redux';
import { MultiAxisDataActions } from '../actions/multiAxisDataActions';
import {
    DrillDownInfo, IAxisItem, IState, KeyPair, TGroupedMeasures
} from '../store/types/StateTypes';
import { MetadataActions } from '../actions/metadataActions';
import datasetTypes from '../enums/datasetTypes';
import multiAxisLevelLayoutAccessor from '../store/accessors/multiAxisLevelLayout';
import visitMultiAxisDatasetNode from '../store/util/visitMultiAxisDatasetNode';
import { IReducerAction } from '../store/types/ReducerTypes';
import getMultiAxisDatasetNode from '../store/util/getMultiAxisDatasetNode';
import {
    IFiltersDataLayout,
    IMultiAxisDataServiceDataLayoutItem,
    IMultiAxisDataServiceDataLayoutMemberExpansion,
    IMultiAxisDataset,
    IMultiAxisDatasetNode,
    MultiAxisDataServiceDataLayoutAxis
} from '../store/types/MultiAxisDatasetTypes';
import missingIndexVisitor from '../components/visualizations/crosstab/util/MissingIndexVisitor';
import DataState from '../enums/DataState';
import { areLayoutsSame } from '../services/QueryExecutionMultiAxisDataService';

/* eslint-disable no-use-before-define */

function toSummaryItem(level: IAxisItem) {
    return {
        id: level.id,
        field: level.field,
        hierarchicalName: level.hierarchicalName,
        expanded: false,
        measuresLevel: level.kind === 'measures',
        type: level.type,
        format: level.format,
        excludeSummary: !level.includeAll,
        categorizer: level.categorizer
    }
}

function setExpanded(metadataItems: any[], queryItems: any[] = [], isCrosstab :boolean) {

    const levelExpansions = queryItems.map((item) => (item.level));

    levelExpansions.forEach((expansion: any) => {
        if (!expansion) {
            return;
        }
        const metadataLevel :any = metadataItems.find((it) => (it.hierarchicalName === expansion.fieldRef || it.id === expansion.fieldRef));

        if (!metadataLevel) {
            throw new Error(`Cannot apply level expansion: ${expansion.fieldRef}`);
        }

        metadataLevel.expanded = expansion.expanded;
    });

    if (metadataItems.length === 0) {
        // no need to continue unless we have some items
        return;
    }

    // we scan the items from the end to the beginning and once we spot the expanded item
    // we set to expanded state all previous items
    for (let i = metadataItems.length - 1; i > 0; i -= 1) {
        if (metadataItems[i].expanded) {
            metadataItems[i - 1].expanded = true;
        }
    }

    if (isCrosstab) {
        // first level expand/collapse hack:
        // we need to shift `expanded` value from i-th to (i+1)th item:
        for (let i = metadataItems.length - 1; i > 0; i -= 1) {
            metadataItems[i].expanded = metadataItems[i - 1].expanded;
        }

        // then for the first item we set expanded to true.
        metadataItems[0].expanded = true;
        // end of the hack
    }

    for (let i = 0; i < metadataItems.length - 1; i += 1) {
        if (metadataItems[i].expanded && metadataItems[i + 1].excludeSummary && !metadataItems[i + 1].measuresLevel) {
            metadataItems[i + 1].expanded = true;
        }
    }
}

function expansionPathMatch(memberExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion, expansions: IMultiAxisDataServiceDataLayoutMemberExpansion[]) {
    return expansions.filter((eX) => (memberExpansion.path.reduce((memo: boolean, memberEXPathItem: string, memberEXPathIndex: number) => (
        memo && memberEXPathItem === eX.path[memberEXPathIndex]
    ), true)))
}

export function putMemberExpansion(axis: MultiAxisDataServiceDataLayoutAxis, memberExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion) {
    const { expansions, items } = axis;
    let res;

    const existingAndSiblings = expansionPathMatch(memberExpansion, expansions);
    const existing = existingAndSiblings.find((eX) => (eX.path.length === memberExpansion.path.length));

    if (existing) {
        res = expansions.reduce((memo, eX) => {
            if (eX === existing) {
                if (memberExpansion.expanded !== items[memberExpansion.path.length].expanded) {
                    memo.push(memberExpansion);
                }
            } else {
                memo.push(eX);
            }

            return memo;
        }, [] as IMultiAxisDataServiceDataLayoutMemberExpansion[]);
    } else {
        res = [...expansions, memberExpansion];
    }

    return res;
}

function mergeNodeChildren(existingChildren: IMultiAxisDatasetNode[], loadedChildren: IMultiAxisDatasetNode[]): IMultiAxisDatasetNode[] {
    let existingCounter = existingChildren.length - 1;
    let loadedCounter = 0;
    let res,
        mergedNode = null;

    while (existingCounter >= 0 && existingChildren[existingCounter].label !== loadedChildren[loadedCounter].label) {
        existingCounter -= 1;
    }

    if (existingCounter < 0) {
        throw new Error('First loaded node was not found in existing nodes.');
    } else {
        res = existingChildren.slice(0, existingCounter);
        do {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            mergedNode = mergeMultiAxisDataTrees(existingChildren[existingCounter], loadedChildren[loadedCounter]);
            if (mergedNode) {
                res.push(mergedNode);
            }
            existingCounter += 1;
            loadedCounter += 1;
        } while (mergedNode);
    }

    return res;
}

function mergeMultiAxisDataTrees(existing: IMultiAxisDatasetNode | null, loaded: IMultiAxisDatasetNode | null): IMultiAxisDatasetNode | null {
    let res = null;

    if (existing && loaded) {
        res = existing;
        if (existing.label === loaded.label) {
            res.isFirst = existing.isFirst || loaded.isFirst;
            res.isLast = existing.isLast || loaded.isLast;

            if (existing.children.length) {
                if (loaded.children.length) {
                    res.children = mergeNodeChildren(existing.children, loaded.children);
                }
            } else if (loaded.children.length) {
                res.children = loaded.children;
            }

            res.total = mergeMultiAxisDataTrees(existing.total, loaded.total);
        } else {
            throw new Error('Labels do not match.');
        }
    } else {
        res = existing || loaded;
    }

    return res;
}

function mergeData(existing: Array<Array<any>>, loaded: Array<Array<any>>, colIndexes: number[], rowIndexes: number[]) {
    for (let i = 0; i < rowIndexes.length; i += 1) {
        if (!existing[rowIndexes[i]]) {
            // eslint-disable-next-line no-param-reassign
            existing[rowIndexes[i]] = [];
        }
        for (let j = 0; j < colIndexes.length; j += 1) {
            // eslint-disable-next-line no-param-reassign
            existing[rowIndexes[i]][colIndexes[j]] = loaded[j][i];
        }
    }
    return existing;
}

function makeIndexFunction(baseIndex: number): () => number {
    let store = baseIndex;
    return () => {
        store += 1;
        return store - 1;
    }
}

function applyExistingIndexes(existing: IMultiAxisDatasetNode | null, loaded: IMultiAxisDatasetNode | null, indexFn: () => number) {
    let res = null;

    if (loaded) {
        res = loaded;
        if (existing) {
            if (existing.label === loaded.label) {
                res.index = existing.index;
            }

            if (loaded.children.length) {
                let loadedCounter = 0;
                let existingCounter = existing.children.length - 1;
                while (existingCounter >= 0 && existing.children[existingCounter].label !== loaded.children[loadedCounter].label) {
                    existingCounter -= 1;
                }

                if (existingCounter < 0 && existing.children.length) {
                    throw new Error('Cannot find any same nodes.')
                } else {
                    do {
                        applyExistingIndexes(existing.children[existingCounter], loaded.children[loadedCounter], indexFn);

                        loadedCounter += 1;
                        existingCounter += 1
                    } while (loadedCounter < loaded.children.length);
                }
            }

            applyExistingIndexes(existing.total, loaded.total, indexFn);

        } else if (res.index === -2) {
            res.index = indexFn();
        } else {
            loaded.children.forEach((child) => { applyExistingIndexes(null, child, indexFn) });
            applyExistingIndexes(null, loaded.total, indexFn);
        }
    }
}

function indexes(root: IMultiAxisDatasetNode) {
    return visitMultiAxisDatasetNode(root, {
        preVisitNode: (node, context) => {
            if (node.index >= 0) {
                context.indexes.push(node.index)
            }
            return context;
        }
    }, { indexes: [] as number[] }).indexes;
}

function missingIndex(root: IMultiAxisDatasetNode): number {
    return visitMultiAxisDatasetNode(root, missingIndexVisitor, {
        indexStack: [],
        currentIndex: -1,
        missingIndex: -1
    }).missingIndex;
}

function areAggregationsComplete(columns: IMultiAxisDatasetNode, rows: IMultiAxisDatasetNode, aggregations: Array<Array<any>>): boolean {
    const columnIndexes = indexes(columns),
        rowIndexes = indexes(rows);

    for (let i = rowIndexes.length - 1; i >= 0; i -= 1) {
        for (let j = columnIndexes.length - 1; j >= 0; j -= 1) {
            if (aggregations[rowIndexes[i]][columnIndexes[j]] === undefined) {
                return false;
            }
        }
    }
    return true;
}

function readMemberExpansions(expansions: any[], items: (IAxisItem | TGroupedMeasures)[]) {
    const measuresIndex = items.findIndex((item) => (item.kind === 'measures'));
    return expansions ? expansions
        .filter((item) => (item.member))
        .map((item) => ({
            expanded: item.member.expanded,
            path: item.member.path
                .map((p: any) => (p === 'empty_node' ? null : p))
                .map((p: any, index: number) => {
                    if (index === measuresIndex) {
                        const measure = (items[index] as TGroupedMeasures).measures.find((m: any) => (m.label === p)) as IAxisItem;
                        if (measure) {
                            return measure.hierarchicalName;
                        }
                    }
                    return p;
                })
        })) : [];
}

export function resetMultiAxisDataSet(state: IState, metadataObj: any) {
    const multiAxisLayout = multiAxisLevelLayoutAccessor(state);

    const measuresLevel: TGroupedMeasures = multiAxisLayout.columns.concat(multiAxisLayout.rows)
        .find((level) => (level.kind === 'measures')) as TGroupedMeasures;

    const measures = (measuresLevel && measuresLevel.measures) || [];
    const rowItems = (multiAxisLayout.rows as IAxisItem[]).map(toSummaryItem);
    const colItems = (multiAxisLayout.columns as IAxisItem[]).map(toSummaryItem);

    const multiAxisQuery = metadataObj.query.multiAxis;

    let rowMemberExpansions: any[],
        columnMemberExpansions: any[];

    if (multiAxisQuery) {
        const isCrosstab = state.visualizationDescriptor.name === 'Crosstab';

        setExpanded(rowItems, multiAxisQuery.groupBy.rows.expansions, isCrosstab);
        setExpanded(colItems, multiAxisQuery.groupBy.columns.expansions, isCrosstab);

        rowMemberExpansions = readMemberExpansions(multiAxisQuery.groupBy.rows.expansions, multiAxisLayout.rows);
        columnMemberExpansions = readMemberExpansions(multiAxisQuery.groupBy.columns.expansions, multiAxisLayout.columns);
    } else {
        rowMemberExpansions = [];
        columnMemberExpansions = [];

        // set default expanded state - everything is collapsed except first level
        if (rowItems[0]) {
            rowItems[0].expanded = true;
        }
        if (colItems[0]) {
            colItems[0].expanded = true;
        }
    }

    return {
        ...state,
        dataState: DataState.LOADING,
        dataError: null,
        data: {
            ...state.data,
            [datasetTypes.MULTI_AXIS]: {
                summary: {
                    rows: {
                        complete: false,
                        empty: false,
                        items: rowItems,
                        expansions: rowMemberExpansions
                    },
                    columns: {
                        complete: false,
                        empty: false,
                        items: colItems,
                        expansions: columnMemberExpansions
                    },
                    aggregations: (measures as IAxisItem[]).map((measure: IAxisItem) => ({
                        ...measure,
                        field: measure.hierarchicalName,
                        aggregationType: measure.aggregationType || measure.type
                    })),
                    filters: {
                        expression: state.filterExpression,
                        parameters: state.filterParameters
                    },
                    order: state.orderInformation,
                    limit: state.dataLimit
                },
                data: {
                    rows: {
                        index: rowItems[0] ? -1 : 0,
                        label: '',
                        expanded: rowItems[0] ? rowItems[0].expanded : true,
                        children: []
                    },
                    columns: {
                        index: colItems[0] ? -1 : 0,
                        label: '',
                        expanded: colItems[0] ? colItems[0].expanded : true,
                        children: []
                    },
                    aggregations: []
                }
            }
        }
    };
}

function getEffectiveLength(orderMember: any) {
    let index = orderMember.path.length - 1;
    while (index >= 0 && orderMember.path[index] === 'All') index -= 1;
    return index;
}

function updateSummary(dataset: IMultiAxisDataset) {
    const {
        summary: {
            empty, complete,
            rows: {
                complete: oldRowsComplete
            },
            columns: {
                complete: oldColumnsComplete
            }
        },
        data: {
            columns, rows, aggregations
        }
    } = dataset;

    const columnsComplete = empty || missingIndex(dataset.data.columns) === -1;
    const rowsComplete = empty || missingIndex(rows) === -1;
    const aggregationsComplete = empty || (columnsComplete && rowsComplete && areAggregationsComplete(columns, rows, aggregations));

    const res = dataset;

    if (complete !== aggregationsComplete || oldRowsComplete !== rowsComplete || oldColumnsComplete !== columnsComplete) {
        res.summary = {
            ...dataset.summary,
            complete: aggregationsComplete,
            rows: {
                ...dataset.summary.rows,
                complete: rowsComplete
            },
            columns: {
                ...dataset.summary.columns,
                complete: columnsComplete
            }
        }
    }
}

function createFilter(pair: KeyPair, index: number): any {
    const filterId = `drilldown_filter_${index}`;
    const { name, value } = pair;
    let filter = null;

    if (name) {
        filter = {
            function:
        {
            operands:
                [{
                    string:
                        { value: filterId }
                },
                {
                    string:
                        { value: 'DYNAMIC' }
                },
                {
                    equals: {
                        operands:
                            [
                                { variable: { name } },

                                {
                                    string: { value }
                                }

                            ]
                    }
                }],
            functionName: 'filter'
        }
        }
    }
    return filter;
}

function combineNodesAnd(op1: any, op2: any): any {
    if (op1 === null || op2 === null) {
        return op1 || op2;
    }

    return {
        and: {
            operands: [op1, op2]
        }
    }
}
function wrapObject(expression: any) {
    if (expression && !expression.object) {
        return {
            object: expression
        }
    }

    return expression;
}

function unwrapObject(expression: any) {
    if (expression && expression.object) {
        return expression.object
    }

    return expression;
}

function applyDrillDownPathToFilters(filtersLayout: IFiltersDataLayout, info: DrillDownInfo): IFiltersDataLayout {
    let result = filtersLayout;

    const expressionArray = info.rowFilter.map(createFilter);
    if (filtersLayout.expression) {
        expressionArray.push(unwrapObject(filtersLayout.expression));
    }

    if (expressionArray.length) {
        const expression = wrapObject(expressionArray.reduce((memo, exp) => (
            memo !== exp ? combineNodesAnd(memo, exp) : memo
        )));

        if (expression) {
            result = {
                ...filtersLayout,
                expression
            }
        }
    }

    return result;
}

export function resetMultiAxisDataSetDrillDown(state: IState, info: DrillDownInfo): IState {
    const multiAxisLayout = multiAxisLevelLayoutAccessor(state);

    const measuresLevel: TGroupedMeasures = multiAxisLayout.columns.concat(multiAxisLayout.rows)
        .find((level) => (level.kind === 'measures')) as TGroupedMeasures;

    const measures = (measuresLevel && measuresLevel.measures) || [];
    const rowItems = (multiAxisLayout.rows as IAxisItem[]).map(toSummaryItem);
    const colItems = state.data[datasetTypes.MULTI_AXIS].summary.columns.items;

    const rowMemberExpansions: any[] = [],
        columnMemberExpansions: any[] = [];

    // eslint-disable-next-line
    rowItems.forEach((item, index) => { item.expanded = index < info.rowLevel + 1 });

    const filters = applyDrillDownPathToFilters({
        expression: state.filterExpression,
        parameters: state.filterParameters
    }, info);

    return {
        ...state,
        dataState: DataState.LOADING,
        dataError: null,
        drillDown: [...(state.drillDown || []), {
            info,
            previousDataset: state.data[datasetTypes.MULTI_AXIS]
        }],
        data: {
            ...state.data,
            [datasetTypes.MULTI_AXIS]: {
                summary: {
                    rows: {
                        complete: false,
                        empty: false,
                        items: rowItems,
                        expansions: rowMemberExpansions
                    },
                    columns: {
                        complete: false,
                        empty: false,
                        items: colItems,
                        expansions: columnMemberExpansions
                    },
                    aggregations: (measures as IAxisItem[]).map((measure: IAxisItem) => ({
                        id: measure.id || measure.hierarchicalName,
                        field: measure.hierarchicalName,
                        aggregationType: measure.type,
                        aggregationFunctionName: measure.aggregationFunctionName,
                        timeBalanceFunctionName: measure.timeBalanceFunctionName
                    })),
                    filters,
                    order: state.orderInformation,
                    limit: state.dataLimit
                },
                data: {
                    rows: {
                        index: rowItems[0] ? -1 : 0,
                        label: '',
                        expanded: rowItems[0] ? rowItems[0].expanded : true,
                        children: []
                    },
                    columns: {
                        index: colItems[0] ? -1 : 0,
                        label: '',
                        expanded: colItems[0] ? colItems[0].expanded : true,
                        children: []
                    },
                    aggregations: []
                }
            }
        }
    };
}

function getNodeByPath(isColumns: boolean, existingDataset: IMultiAxisDataset, memberExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion) {
    let node;
    if (isColumns) {
        node = getMultiAxisDatasetNode(existingDataset.data.columns, memberExpansion.path).pop();
    } else {
        node = getMultiAxisDatasetNode(existingDataset.data.rows, memberExpansion.path).pop();
    }
    return node;
}

function removeExpandedStateFromNodes(root: IMultiAxisDatasetNode, memberExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion, levels: IMultiAxisDataServiceDataLayoutItem[]) {
    const expansionNode = getMultiAxisDatasetNode(root, memberExpansion.path).pop();

    if (expansionNode) {
        const pathLength = memberExpansion.path.length;
        visitMultiAxisDatasetNode(expansionNode, {
            preVisitNode(nd, context, curretDepth) {
                const node = nd;
                node.expanded = levels[pathLength + curretDepth] && levels[pathLength + curretDepth].expanded;
                return context;
            }
        }, null, true);
    }
}

function removeOrphanExpansions(expansions: IMultiAxisDataServiceDataLayoutMemberExpansion[],
    memberExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion,
    root: IMultiAxisDatasetNode, items: IMultiAxisDataServiceDataLayoutItem[]) {

    const existingAndSiblings = expansionPathMatch(memberExpansion, expansions);
    const siblings = existingAndSiblings.filter((eX) => (eX.path.length !== memberExpansion.path.length));

    return expansions.reduce((memo, eX) => {
        if (siblings.find((sibling) => (sibling === eX))) {
            removeExpandedStateFromNodes(root, eX, items);
        } else {
            memo.push(eX);
        }

        return memo;
    }, [] as IMultiAxisDataServiceDataLayoutMemberExpansion[]);
}

function filterOutMemberExpansion(memberExpansions: IMultiAxisDataServiceDataLayoutMemberExpansion[], levelNumber: number, expanded: boolean) {
    return memberExpansions.filter((expansion: any) => {
        const higherLevel = !expanded && expansion.path.length < levelNumber;
        const lowerLevelExpanded = expanded && (expansion.path.length > levelNumber) && expansion.expanded;

        return higherLevel || lowerLevelExpanded;
    });
}

function applyExpansionStateToNodes(rootNode: IMultiAxisDatasetNode, axisSummary: MultiAxisDataServiceDataLayoutAxis) {
    const { items, expansions } = axisSummary;

    // apply new value to existing nodes
    visitMultiAxisDatasetNode(rootNode, {
        postVisitNode: (node, context, currentDepth) => {
            if (items[currentDepth]) {
                node.expanded = items[currentDepth].expanded; // eslint-disable-line no-param-reassign
            }

            return context;
        }
    }, {}, true);
    // restore values of higher level member expansions
    expansions.forEach((memberExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion) => {
        const node = getMultiAxisDatasetNode(rootNode, memberExpansion.path).pop();
        if (node) {
            node.expanded = memberExpansion.expanded;
        }
    });
}

export const multiAxisDataReducer: Reducer<any, IReducerAction<any>> = (state: IState, action: IReducerAction<any>) => {
    let newState = state;

    if (action.type === MetadataActions.METADATA_LOADED) {
        // discard dataset because metadata was updated
        newState = resetMultiAxisDataSet(state, action.data.metadataObj);
    }

    if (action.type === MultiAxisDataActions.MULTI_AXIS_DATA_LOADED) {
        // conversion of the response to state happens here
        const existingDataset = state.data[datasetTypes.MULTI_AXIS];
        const loadedDataset = action.data.dataObj;

        const rowLastIndex = existingDataset.data.aggregations.length;
        const colLastIndex = existingDataset.data.aggregations.reduce((memo: number, agArr: any[]) => (Math.max(memo, agArr.length)), 0);

        applyExistingIndexes(existingDataset.data.rows, loadedDataset.data.rows, makeIndexFunction(rowLastIndex));
        applyExistingIndexes(existingDataset.data.columns, loadedDataset.data.columns, makeIndexFunction(colLastIndex));

        const columns = mergeMultiAxisDataTrees(existingDataset.data.columns, loadedDataset.data.columns) || existingDataset.data.columns;
        const rows = mergeMultiAxisDataTrees(existingDataset.data.rows, loadedDataset.data.rows) || existingDataset.data.rows;
        const aggregations = mergeData(existingDataset.data.aggregations, loadedDataset.data.aggregations, indexes(loadedDataset.data.columns), indexes(loadedDataset.data.rows));

        if (!areLayoutsSame(existingDataset.summary, loadedDataset.summary)) {
            applyExpansionStateToNodes(columns, state.data[datasetTypes.MULTI_AXIS].summary.columns);
            applyExpansionStateToNodes(rows, state.data[datasetTypes.MULTI_AXIS].summary.rows);
        }

        newState = {
            ...state,
            dataState: DataState.LOADED,
            dataError: null,
            data: {
                ...state.data,
                [datasetTypes.MULTI_AXIS]: {
                    ...state.data[datasetTypes.MULTI_AXIS],
                    data: { columns, rows, aggregations }
                }
            }
        };

        newState.data[datasetTypes.MULTI_AXIS].summary.empty = loadedDataset.summary.empty;
        updateSummary(newState.data[datasetTypes.MULTI_AXIS]);
    }

    if (action.type === MultiAxisDataActions.MULTI_AXIS_DATA_ERROR) {
        newState = {
            ...state,
            dataState: DataState.ERROR,
            dataError: action.errorDescriptor
        }
    }

    if (action.type === MultiAxisDataActions.MULTI_AXIS_SET_LEVEL_EXPANDED_STATE) {
        const multiAxisLayout = multiAxisLevelLayoutAccessor(state);
        const existingDataset = state.data[datasetTypes.MULTI_AXIS];
        const orderObj = existingDataset.summary.order;
        const order = orderObj.members.concat(orderObj.topN).concat(orderObj.bottomN);
        let resetDataset = false;

        let level = multiAxisLayout.columns.find((column: IAxisItem | TGroupedMeasures) => ((column as IAxisItem).id === action.data.levelId));
        const columns = !!level;

        if (!columns) {
            level = multiAxisLayout.rows.find((row: IAxisItem | TGroupedMeasures) => ((row as IAxisItem).id === action.data.levelId));
        }

        if (!level) {
            throw new Error(`Cannot find level: ${action.data.levelId}`);
        }

        const { expanded } = action.data;
        const newRows = existingDataset.summary.rows.items;
        const newCols = existingDataset.summary.columns.items;
        const newRowsEx = existingDataset.summary.rows.expansions;
        const newColsEx = existingDataset.summary.columns.expansions;
        const newDataRows = existingDataset.data.rows;
        const newDataCols = existingDataset.data.columns;
        const axisToChange = columns ? newCols : newRows;
        const dataToChange = columns ? newDataCols : newDataRows;
        const levelNumber = (columns ? multiAxisLayout.columns : multiAxisLayout.rows).indexOf(level);
        const tmpAxis = [] as any[];
        const tmpExp = filterOutMemberExpansion(columns ? newColsEx : newRowsEx, levelNumber, expanded)

        if (columns && order.length) {
            const [orderMember] = order;
            const effectiveLength = getEffectiveLength(orderMember);
            resetDataset = expanded ? levelNumber === effectiveLength : levelNumber <= effectiveLength;
        }

        for (let i = 0; i < axisToChange.length; i += 1) {
            // all level above should be expanded, all levels below should be collapsed,
            // value for the actual level is taken from action
            tmpAxis[i] = {
                ...axisToChange[i],
                expanded: expanded ? i <= levelNumber : i < levelNumber
            };
        }

        const newSummary = {
            ...existingDataset.summary,
            rows: {
                ...existingDataset.summary.rows,
                items: columns ? newRows : tmpAxis,
                expansions: columns ? newRowsEx : tmpExp
            },
            columns: {
                ...existingDataset.summary.columns,
                items: columns ? tmpAxis : newCols,
                expansions: columns ? tmpExp : newColsEx
            },
        };

        let newDataset;
        if (resetDataset) {
            newDataset = {
                aggregations: [],
                rows: {
                    ...newDataRows,
                    children: [],
                    total: null
                },
                columns: {
                    ...newDataCols,
                    children: [],
                    total: null
                }
            }
        } else {
            // apply new value to existing nodes
            visitMultiAxisDatasetNode(dataToChange, {
                postVisitNode: (node, context, currentDepth) => {
                    if (axisToChange[currentDepth]) {
                        node.expanded = tmpAxis[currentDepth].expanded; // eslint-disable-line no-param-reassign
                    }

                    return context;
                }
            }, {}, true);
            // restore values of higher level member expansions
            tmpExp.forEach((memberExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion) => {
                const node = getNodeByPath(columns, existingDataset, memberExpansion);
                if (node) {
                    node.expanded = memberExpansion.expanded;
                }
            })

            newDataset = {
                ...existingDataset.data,
                rows: newDataRows,
                columns: newDataCols
            }
        }

        newState = {
            ...state,
            data: {
                ...state.data,
                [datasetTypes.MULTI_AXIS]: {
                    summary: newSummary,
                    data: newDataset
                }
            }
        };

        updateSummary(newState.data[datasetTypes.MULTI_AXIS]);
    }

    if (action.type === MultiAxisDataActions.MULTI_AXIS_SET_MEMBER_EXPANDED_STATE) {
        const { memberExpansion, isColumns } = action.data;
        const existingDataset = state.data[datasetTypes.MULTI_AXIS];
        let resetDataset = false;
        const node = getNodeByPath(isColumns, existingDataset, memberExpansion);

        if (node) {
            node.expanded = memberExpansion.expanded;

            let newSummary,
                newDataset;
            if (isColumns) {
                let newExpansions = putMemberExpansion(existingDataset.summary.columns, memberExpansion);
                newExpansions = removeOrphanExpansions(newExpansions, memberExpansion, existingDataset.data.columns, existingDataset.summary.columns.items);

                newSummary = {
                    ...existingDataset.summary,
                    columns: {
                        ...existingDataset.summary.columns,
                        expansions: newExpansions
                    }
                };

                const order = newSummary.order.members.concat(newSummary.order.topN).concat(newSummary.order.bottomN);
                if (order.length) {
                    const orderPath = order[0].path;
                    const effectiveLength = getEffectiveLength(order[0]);
                    const levelNumber = memberExpansion.path.length;
                    const canBeAffected = levelNumber <= effectiveLength;

                    if (canBeAffected) {
                        resetDataset = memberExpansion.path.reduce((memo: boolean, p: string, index: number) => (memo && (p === orderPath[index])), true);
                    }
                }
            } else {
                let newExpansions = putMemberExpansion(existingDataset.summary.rows, memberExpansion);
                newExpansions = removeOrphanExpansions(newExpansions, memberExpansion, existingDataset.data.rows, existingDataset.summary.rows.items);

                newSummary = {
                    ...existingDataset.summary,
                    rows: {
                        ...existingDataset.summary.rows,
                        expansions: newExpansions
                    }
                };
            }

            if (resetDataset) {
                newDataset = {
                    aggregations: [],
                    rows: {
                        ...existingDataset.data.rows,
                        children: [],
                        total: null
                    },
                    columns: {
                        ...existingDataset.data.rows,
                        children: [],
                        total: null
                    }
                }
            } else {
                newDataset = existingDataset.data;
            }

            newState = {
                ...state,
                data: {
                    ...state.data,
                    [datasetTypes.MULTI_AXIS]: {
                        summary: newSummary,
                        data: newDataset
                    }
                }
            };

            updateSummary(newState.data[datasetTypes.MULTI_AXIS]);
        }
    }

    if (action.type === MultiAxisDataActions.MULTI_AXIS_SET_MEMBERS_EXPANDED_STATE) {
        const { rowExpansions, colExpansions } = action.data;
        const existingDataset = state.data[datasetTypes.MULTI_AXIS];

        let resetDataset = false;
        let newSummary = existingDataset.summary;
        let newDataset;

        rowExpansions.forEach((memberExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion) => {
            const node = getNodeByPath(false, existingDataset, memberExpansion);

            if (node) {
                node.expanded = memberExpansion.expanded;
                let newExpansions = putMemberExpansion(newSummary.rows, memberExpansion);
                newExpansions = removeOrphanExpansions(newExpansions, memberExpansion, existingDataset.data.rows, existingDataset.summary.rows.items);

                newSummary = {
                    ...newSummary,
                    rows: {
                        ...newSummary.rows,
                        expansions: newExpansions
                    }
                };

            }
        });

        colExpansions.forEach((memberExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion) => {
            const node = getNodeByPath(true, existingDataset, memberExpansion);

            if (node) {
                node.expanded = memberExpansion.expanded;
                let newExpansions = putMemberExpansion(newSummary.columns, memberExpansion);
                newExpansions = removeOrphanExpansions(newExpansions, memberExpansion, existingDataset.data.columns, existingDataset.summary.columns.items);

                newSummary = {
                    ...newSummary,
                    columns: {
                        ...newSummary.columns,
                        expansions: newExpansions
                    }
                };
            }
        });

        const order = newSummary.order.members.concat(newSummary.order.topN).concat(newSummary.order.bottomN);
        if (order.length) {
            const orderPath = order[0].path;
            const effectiveLength = getEffectiveLength(order[0]);

            resetDataset = newSummary.columns.expansions.reduce((isResetNesessary: boolean, mExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion) => {
                const levelNumber = mExpansion.path.length;
                const canBeAffected = levelNumber <= effectiveLength;

                if (canBeAffected) {
                    return isResetNesessary || mExpansion.path.reduce((memo: boolean, p: string, index: number) => (memo && (p === orderPath[index])), true);
                }

                return isResetNesessary;

            }, false);
        }

        if (resetDataset) {
            newDataset = {
                aggregations: [],
                rows: {
                    ...existingDataset.data.rows,
                    children: [],
                    total: null
                },
                columns: {
                    ...existingDataset.data.rows,
                    children: [],
                    total: null
                }
            }
        } else {
            newDataset = existingDataset.data;
        }

        newState = {
            ...state,
            data: {
                ...state.data,
                [datasetTypes.MULTI_AXIS]: {
                    summary: newSummary,
                    data: newDataset
                }
            }
        };

        updateSummary(newState.data[datasetTypes.MULTI_AXIS]);
    }

    if (action.type === MultiAxisDataActions.MULTI_AXIS_DRILLDOWN_TO) {
        if (state.drillDown && action.data.info) {
            const previousDrilldown = state.drillDown.find((drilldownLevel) => (drilldownLevel.info.rowLevel === action.data.info.rowLevel));
            if (previousDrilldown) {
                const newDrilldown = [];
                for (let i = 0; state.drillDown[i] !== previousDrilldown; i += 1) {
                    newDrilldown.push(state.drillDown[i]);
                }

                newState = {
                    ...state,
                    dataState: DataState.LOADING,
                    dataError: null,
                    drillDown: newDrilldown,
                    data: {
                        ...state.data,
                        [datasetTypes.MULTI_AXIS]: previousDrilldown.previousDataset
                    }
                }
            } else {
                newState = resetMultiAxisDataSetDrillDown(state, action.data.info)
            }

            newState.lastEvent = {
                name: 'drilldown',
                payload: newState.drillDown?.map((drillDownLevel) => (drillDownLevel.info))
            }
        }
    }

    return newState;
};
